/**
 * Formatting for invoicing screens (design handoff "Formatting rules"):
 * money `Rs. 48,500.00`, bare `48,500.00` in A4 line columns, dates
 * `22 Oct 2026`, times `22 Oct 2026, 14:30`, relative `2 h ago`.
 * Money arrives as integer minor units and is never divided as a float.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** 4850000 → "48,500.00"; negatives keep a leading minus sign. */
export function num(minor: number | null | undefined): string {
  const m = Math.round(Number(minor) || 0);
  const sign = m < 0 ? '−' : '';
  const abs = Math.abs(m);
  const whole = Math.floor(abs / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${sign}${whole}.${String(abs % 100).padStart(2, '0')}`;
}

export const money = (minor: number | null | undefined, currency = 'LKR') =>
  `${currency === 'LKR' ? 'Rs.' : currency} ${num(minor)}`;

/** "1,234.5" or "1234.50" typed by a user → minor units; NaN when not a number. */
export function parseMoney(text: string): number {
  const clean = String(text ?? '').replace(/[,\s]/g, '').replace(/^Rs\.?/i, '');
  if (!/^\d*(\.\d{0,2})?$/.test(clean) || clean === '' || clean === '.') return NaN;
  const [whole, frac = ''] = clean.split('.');
  return Number(whole || '0') * 100 + Number(frac.padEnd(2, '0'));
}

/** Minor units → the editable text of a money input ("1500.00" → "1500"). */
export function moneyInput(minor: number | null | undefined): string {
  const m = Math.round(Number(minor) || 0);
  return m % 100 === 0 ? String(m / 100) : num(m).replace(/,/g, '');
}

export function date(iso?: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

export function dateTime(ms?: number | null): string {
  if (!ms) return '—';
  const d = new Date(ms);
  const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return `${date(iso)}, ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function ago(ms?: number | null, now = Date.now()): string {
  if (!ms) return '';
  const min = (now - ms) / 60000;
  if (min < 1) return 'just now';
  if (min < 60) return `${Math.round(min)} min ago`;
  if (min < 1440) return `${Math.round(min / 60)} h ago`;
  const days = Math.round(min / 1440);
  return days === 1 ? 'yesterday' : `${days} days ago`;
}

/** Today in Asia/Colombo as YYYY-MM-DD (matches the server's "today"). */
export function todayColombo(now = Date.now()): string {
  return new Date(now + 330 * 60 * 1000).toISOString().slice(0, 10);
}

export function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export const daysBetween = (a: string, b: string) =>
  Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 864e5);

/** "12 days overdue" / "Due today" / "In 9 days" for a due date. */
export function dueHint(due?: string | null, today = todayColombo()): { text: string; overdue: boolean } | null {
  if (!due) return null;
  const d = daysBetween(today, due);
  if (d < 0) return { text: `${-d} day${d === -1 ? '' : 's'} overdue`, overdue: true };
  if (d === 0) return { text: 'Due today', overdue: false };
  return { text: `In ${d} day${d === 1 ? '' : 's'}`, overdue: false };
}

export const TERMS: { value: string; label: string; days: number | null }[] = [
  { value: 'DUE_ON_RECEIPT', label: 'Due on receipt', days: 0 },
  { value: 'NET_7', label: 'Net 7', days: 7 },
  { value: 'NET_15', label: 'Net 15', days: 15 },
  { value: 'NET_30', label: 'Net 30', days: 30 },
  { value: 'CUSTOM', label: 'Custom', days: null },
];

export const termLabel = (terms?: string, customDays?: number) => {
  if (terms === 'CUSTOM') return `${customDays ?? 0} days`;
  return TERMS.find((t) => t.value === terms)?.label ?? '—';
};

export const termDays = (terms?: string, customDays?: number) =>
  terms === 'CUSTOM' ? customDays ?? 0 : TERMS.find((t) => t.value === terms)?.days ?? 0;

export const METHOD_LABEL: Record<string, string> = {
  CASH: 'Cash', BANK_TRANSFER: 'Bank transfer', CHEQUE: 'Cheque', CARD: 'Card', LANKAQR: 'LankaQR', OTHER: 'Other',
};

/** Quantity without trailing zeros: 2.500 → "2.5". */
export const qty = (q: number) => String(Math.round(q * 1000) / 1000);
